/**
 * ConnectMeta Component
 * Two ways to connect:
 * Option A — Manual: Enter Pixel ID → Verify → Paste token in dashboard
 * Option B — OAuth: Enter Pixel ID → Connect with Meta → Auto token (commented out — pending Meta permission fix)
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useToast } from '../analytics/Toast';
import './ConnectMeta.css';

const ConnectMeta = ({ onConnect }) => {
  const [pixelId, setPixelId]             = useState('');
  const [pixelName, setPixelName]         = useState('');
  const [isSaving, setIsSaving]           = useState(false);
  // const [isConnecting, setIsConnecting]   = useState(false); // OAuth — commented out
  const [showHowTo, setShowHowTo]         = useState(false);
  const [pixelSaved, setPixelSaved]       = useState(false);

  const { token } = useAuthToken();
  const { showToast } = useToast();

  // Handle OAuth callback redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const insights = params.get('insights');

    if (error === 'permission_denied') {
      showToast('Meta account connection cancelled', 'error');
    } else if (error === 'auth_failed') {
      showToast('Meta connection failed — please try again', 'error');
    } else if (insights === 'ready') {
      showToast('Meta account connected! Insights are now available.', 'success');
    }

    if (error || insights) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSavePixel = async () => {
    if (!pixelId.trim()) {
      showToast('Please enter your Pixel ID', 'error');
      return;
    }
    const businessId = localStorage.getItem('user_id');
    if (!businessId) {
      showToast('Business ID not found. Please log in again.', 'error');
      return;
    }
    try {
      setIsSaving(true);
      const response = await axios.post(
        `${process.env.REACT_APP_APIURL}meta/pixel/save`,
        { business_id: businessId, pixelId: pixelId.trim(), pixelName: pixelName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showToast('Pixel ID saved!', 'success');
        setPixelSaved(true);
      } else {
        showToast(response.data.message || 'Failed to save Pixel ID', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save Pixel ID', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualContinue = () => {
    if (onConnect) onConnect();
  };

  // ── OAuth handler — commented out (Meta pixel stats requires pixel linked to ad account)
  // const handleConnectMeta = async () => {
  //   try {
  //     setIsConnecting(true);
  //     const businessId = localStorage.getItem('user_id');
  //     if (!businessId) {
  //       showToast('Business ID not found. Please log in again.', 'error');
  //       setIsConnecting(false);
  //       return;
  //     }
  //     if (!token) {
  //       showToast('Authentication token not available. Please refresh the page.', 'error');
  //       setIsConnecting(false);
  //       return;
  //     }
  //     const response = await axios.get(
  //       `${process.env.REACT_APP_APIURL}meta/oauth/auth-url`,
  //       {
  //         params: { business_id: businessId },
  //         headers: { Authorization: `Bearer ${token}` }
  //       }
  //     );
  //     if (response.data.success && response.data.data.authUrl) {
  //       window.location.href = response.data.data.authUrl;
  //     } else {
  //       throw new Error('Failed to get Meta auth URL');
  //     }
  //   } catch (error) {
  //     showToast('Failed to connect Meta account. Please try again.', 'error');
  //     setIsConnecting(false);
  //   }
  // };

  return (
    <div className="meta-connect-wrapper">

      {/* Header Card */}
      <div className="meta-connect-header-card">
        <div className="meta-brand-icons">
          {/* Facebook */}
          <div className="meta-brand-icon meta-brand-icon-fb">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          {/* Instagram */}
          <div className="meta-brand-icon meta-brand-icon-ig">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="#fff" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
          </div>
        </div>
        <div>
          <h1 className="meta-connect-heading">Meta Pixel</h1>
          <p className="meta-connect-subheading"><strong>Facebook & Instagram</strong> · Track visitors and ad performance</p>
        </div>
      </div>

      {/* Benefit chips */}
      <div className="meta-connect-chips">
        <span className="meta-connect-chip">Facebook Ads</span>
        <span className="meta-connect-chip">Instagram Ads</span>
        <span className="meta-connect-chip">Retarget customers</span>
        <span className="meta-connect-chip">Measure ad ROI</span>
      </div>

      {/* Step 1 — Pixel ID */}
      <div className="meta-connect-section">
        <div className="meta-connect-step-label">
          Step 1 — Connect Pixel
          {pixelSaved && <span className="meta-connect-done-badge">✓ Done</span>}
        </div>
        <p className="meta-connect-step-desc">
          Enter your Meta Pixel ID to start tracking store visitors.
        </p>

        <input
          className="meta-connect-input"
          type="text"
          placeholder="Enter Pixel ID (e.g. 2296996577489986)"
          value={pixelId}
          onChange={e => setPixelId(e.target.value.replace(/\D/g, ''))}
          maxLength={20}
          disabled={pixelSaved}
        />

        {pixelId && !pixelSaved && (
          <input
            className="meta-connect-input meta-connect-input-secondary"
            type="text"
            placeholder="Pixel name (optional)"
            value={pixelName}
            onChange={e => setPixelName(e.target.value)}
          />
        )}

        <button
          className="meta-connect-howto-toggle"
          onClick={() => setShowHowTo(!showHowTo)}
        >
          {showHowTo ? '▲' : '▼'} How to find your Pixel ID
        </button>

        {showHowTo && (
          <div className="meta-connect-howto-panel">
            <ol className="meta-connect-howto-steps">
              <li>Go to <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer">Meta Events Manager</a></li>
              <li>Click <strong>Connect data sources</strong> → <strong>Web</strong></li>
              <li>Select <strong>Meta Pixel</strong> → click <strong>Connect</strong></li>
              <li>Name your pixel and click <strong>Create Pixel</strong></li>
              <li>Copy the 16-digit <strong>Pixel ID</strong> and paste above</li>
            </ol>
          </div>
        )}

        {!pixelSaved && (
          <button
            className="meta-connect-btn"
            onClick={handleSavePixel}
            disabled={isSaving || !pixelId.trim()}
          >
            {isSaving ? <span className="meta-spinner-sm" /> : 'Connect Pixel'}
          </button>
        )}
      </div>

      {/* Step 2 — Continue to Verify (shown after pixel saved) */}
      {pixelSaved && (
        <div className="meta-connect-section">
          <div className="meta-connect-step-label">
            Step 2 — Verify Pixel
          </div>
          <p className="meta-connect-step-desc">
            Verify your pixel is working, then add your System User access token in the dashboard to view event insights.
          </p>

          <button
            className="meta-connect-btn"
            onClick={handleManualContinue}
          >
            Continue to Verify Pixel →
          </button>

          {/* ── OAuth Option — commented out (pending Meta pixel stats permission fix) ──
          <div className="meta-connect-divider"><span>or</span></div>
          <div className="meta-connect-option-card">
            <div className="meta-connect-option-header">
              <span className="meta-connect-option-tag meta-connect-option-tag-blue">Auto</span>
              <span className="meta-connect-option-title">Connect with Meta account</span>
            </div>
            <p className="meta-connect-option-desc">
              Sign in with your Meta account to automatically connect and go directly to your dashboard.
            </p>
            <button
              className="meta-connect-oauth-btn"
              onClick={handleConnectMeta}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <><span className="meta-spinner-sm" /> Connecting...</>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Connect with Meta
                </>
              )}
            </button>
          </div>
          ── End OAuth Option ── */}
        </div>
      )}

    </div>
  );
};

export default ConnectMeta;